import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useInvitationStore } from '../../store/invitationStore';
import { 
  X, 
  Users, 
  Clock, 
  Link2, 
  Infinity as InfinityIcon,
  Copy, 
  Check,
  Share2 
} from 'lucide-react';

interface CreateInviteModalProps {
  serverId: string;
  serverName: string;
  onClose: () => void;
}

const inviteUrlForCode = (code: string) => `https://erozion.net/${code}`;

const CreateInviteModal: React.FC<CreateInviteModalProps> = ({ serverId, serverName, onClose }) => {
  const [maxUses, setMaxUses] = useState<number>(0); // 0 = unlimited
  const [expireAfter, setExpireAfter] = useState<number>(0); // 0 = never expires
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  const { createInvitation, isLoading, error } = useInvitationStore();

  const handleCreate = async () => {
    // Convert expireAfter to seconds
    let maxAge = 0;
    if (expireAfter > 0) {
      if (expireAfter === 1) maxAge = 30 * 60; // 30 minutes
      if (expireAfter === 2) maxAge = 60 * 60; // 1 hour
      if (expireAfter === 3) maxAge = 24 * 60 * 60; // 24 hours
      if (expireAfter === 4) maxAge = 7 * 24 * 60 * 60; // 7 days
      if (expireAfter === 5) maxAge = 30 * 24 * 60 * 60; // 30 days
    }
    
    const invitation = await createInvitation(serverId, maxUses, maxAge);
    if (invitation) {
      setInviteCode(invitation.code);
    }
  };
  
  const handleCopy = () => {
    if (!inviteCode) return;
    
    const inviteUrl = inviteUrlForCode(inviteCode);
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };
  
  const handleShare = async () => {
    if (!inviteCode || !navigator.share) return;
    
    const inviteUrl = inviteUrlForCode(inviteCode);
    
    try {
      await navigator.share({
        title: `Join ${serverName} on Erozion`,
        text: `I've invited you to join my server "${serverName}" on Erozion!`,
        url: inviteUrl,
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[200]">
      <div className="bg-bg-secondary rounded-lg w-full max-w-md overflow-hidden animate-fade-in">
        <div className="flex justify-between items-center p-4 border-b border-border-color">
          <h2 className="text-xl font-bold text-text-primary flex items-center">
            <Link2 className="h-5 w-5 mr-2 text-accent-color" />
            {inviteCode ? 'Invitation Created' : 'Create Invitation'}
          </h2>
          <button 
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6">
          {error && (
            <div className="bg-error/20 border border-error text-error p-3 rounded-md mb-4 text-sm">
              {error}
            </div>
          )}
          
          {inviteCode ? (
            <div className="space-y-4">
              <div className="text-center mb-2">
                <h3 className="text-lg font-medium text-text-primary">Invitation to {serverName}</h3>
                <p className="text-text-secondary text-sm">Share this link with others</p>
              </div>
              
              <div className="flex items-center bg-bg-tertiary rounded-lg overflow-hidden p-1">
                <input
                  type="text"
                  value={inviteUrlForCode(inviteCode)}
                  readOnly
                  className="bg-transparent border-0 flex-1 p-2 text-text-primary focus:outline-none"
                />
                
                <button
                  onClick={handleCopy}
                  className="p-2 text-text-primary hover:text-accent-color rounded-md transition-colors"
                  title="Copy link"
                >
                  {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                </button>
              </div>
              
              <div className="flex justify-center gap-4 mt-4">
                <button
                  onClick={onClose}
                  className="btn btn-secondary px-4 py-2"
                >
                  Done
                </button>
                
                {navigator.share && (
                  <button
                    onClick={handleShare}
                    className="btn btn-primary px-4 py-2 flex items-center"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-text-secondary uppercase mb-2 flex items-center">
                  <Users className="h-4 w-4 mr-1" />
                  Invite Usage
                </h3>
                
                <div className="grid grid-cols-2 gap-2">
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      maxUses === 0 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setMaxUses(0)}
                  >
                    <InfinityIcon className="h-5 w-5 mb-1" />
                    <span className="text-sm">Unlimited</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      maxUses === 1 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setMaxUses(1)}
                  >
                    <Users className="h-5 w-5 mb-1" />
                    <span className="text-sm">1 use</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      maxUses === 5 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setMaxUses(5)}
                  >
                    <Users className="h-5 w-5 mb-1" />
                    <span className="text-sm">5 uses</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      maxUses === 10 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setMaxUses(10)}
                  >
                    <Users className="h-5 w-5 mb-1" />
                    <span className="text-sm">10 uses</span>
                  </button>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-text-secondary uppercase mb-2 flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Expiration
                </h3>
                
                <div className="grid grid-cols-2 gap-2">
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      expireAfter === 0 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setExpireAfter(0)}
                  >
                    <InfinityIcon className="h-5 w-5 mb-1" />
                    <span className="text-sm">Never</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      expireAfter === 1 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setExpireAfter(1)}
                  >
                    <Clock className="h-5 w-5 mb-1" />
                    <span className="text-sm">30 minutes</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      expireAfter === 2 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setExpireAfter(2)}
                  >
                    <Clock className="h-5 w-5 mb-1" />
                    <span className="text-sm">1 hour</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      expireAfter === 3 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setExpireAfter(3)}
                  >
                    <Clock className="h-5 w-5 mb-1" />
                    <span className="text-sm">24 hours</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      expireAfter === 4 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setExpireAfter(4)}
                  >
                    <Clock className="h-5 w-5 mb-1" />
                    <span className="text-sm">7 days</span>
                  </button>
                  
                  <button
                    className={`p-2 rounded-md flex flex-col items-center justify-center border transition-all ${
                      expireAfter === 5 
                        ? 'border-accent-color bg-accent-color/10' 
                        : 'border-border-color bg-bg-tertiary hover:bg-bg-primary'
                    }`}
                    onClick={() => setExpireAfter(5)}
                  >
                    <Clock className="h-5 w-5 mb-1" />
                    <span className="text-sm">30 days</span>
                  </button>
                </div>
              </div>
              
              <div className="flex justify-end pt-4">
                <button
                  onClick={onClose}
                  className="btn btn-secondary mr-2"
                >
                  Cancel
                </button>
                
                <button
                  onClick={handleCreate}
                  disabled={isLoading}
                  className="btn btn-primary"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span>Creating...</span>
                    </div>
                  ) : (
                    'Create Invitation'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  , document.body);
};

export default CreateInviteModal;
